import { useState } from "react";
import { useUser, useAuth } from "@clerk/react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authFetch } from "@/lib/api";

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type DeleteResponse = {
  deleted: true;
  counts: Record<string, number>;
} & ({ eventId: string } | { seriesId: string });

type Kind = "event" | "event-series";

const KIND_LABELS: Record<Kind, { noun: string; endpoint: string }> = {
  event: { noun: "event", endpoint: "/admin/events" },
  "event-series": { noun: "event series", endpoint: "/admin/event-series" },
};

export default function AdminPage() {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();

  if (!isLoaded) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  const isAdmin = user?.publicMetadata?.role === "admin";
  if (!user || !isAdmin) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <div className="rounded-lg border bg-card p-8 text-center">
          <h1 className="text-xl font-semibold">Not authorized</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            This page is restricted to admin users.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-10">
      <h1 className="text-2xl font-semibold">Admin</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Destructive actions. Use with care.
      </p>

      <div className="mt-8 space-y-6">
        <DeleteSection
          kind="event"
          title="Delete event"
          description="Permanently deletes an event and all associated race results, splits, and source-specific detail rows."
          getToken={getToken}
        />
        <DeleteSection
          kind="event-series"
          title="Delete event series"
          description="Permanently deletes a series and its rollups. Events linked to the series are unlinked, not deleted."
          getToken={getToken}
        />
      </div>
    </div>
  );
}

function DeleteSection({
  kind,
  title,
  description,
  getToken,
}: {
  kind: Kind;
  title: string;
  description: string;
  getToken: () => Promise<string | null>;
}) {
  const { noun, endpoint } = KIND_LABELS[kind];
  const [id, setId] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<DeleteResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (targetId: string) => {
      const token = await getToken();
      return authFetch<DeleteResponse>(`${endpoint}/${targetId}`, {
        method: "DELETE",
        token,
      });
    },
    onSuccess: (data) => {
      setResult(data);
      setError(null);
      setId("");
      setConfirmOpen(false);
    },
    onError: (err: Error) => {
      setError(err.message);
      setConfirmOpen(false);
    },
  });

  const trimmed = id.trim();
  const isValidUuid = UUID_REGEX.test(trimmed);
  const resolvedId =
    result && "eventId" in result
      ? result.eventId
      : result && "seriesId" in result
        ? result.seriesId
        : null;

  return (
    <section className="rounded-lg border bg-card p-6">
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>

      <form
        className="mt-4 flex flex-col gap-3 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          if (!isValidUuid) return;
          setError(null);
          setResult(null);
          setConfirmOpen(true);
        }}
      >
        <Input
          type="text"
          placeholder={`${noun} UUID`}
          value={id}
          onChange={(e) => setId(e.target.value)}
          className="flex-1 font-mono"
          autoComplete="off"
          spellCheck={false}
        />
        <Button type="submit" variant="destructive" disabled={!isValidUuid}>
          Delete {noun}
        </Button>
      </form>

      {id && !isValidUuid && (
        <p className="mt-2 text-xs text-destructive">Not a valid UUID.</p>
      )}

      {error && (
        <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {result && resolvedId && (
        <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm">
          <div className="font-medium text-emerald-700 dark:text-emerald-300">
            Deleted {noun} {resolvedId}
          </div>
          <ul className="mt-2 space-y-0.5 font-mono text-xs text-muted-foreground">
            {Object.entries(result.counts).map(([key, value]) => (
              <li key={key}>
                {key}: {value}
              </li>
            ))}
          </ul>
        </div>
      )}

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !deleteMutation.isPending && setConfirmOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-lg border bg-card p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold">Confirm delete</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              This permanently deletes the {noun}. This cannot be undone.
            </p>
            <div className="mt-3 rounded-md bg-muted p-2 font-mono text-xs break-all">
              {trimmed}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmOpen(false)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteMutation.mutate(trimmed)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
